import { collection, doc, getDocs, addDoc, query, where, getFirestore, getDoc, updateDoc } from "firebase/firestore";
import { addMissingFullPropertyProperties, FullProperty, PaymentEvent } from "../../models/fullProperty";
import { DistanceSearchItem } from "../../models/search";
import { ProfileType } from "../../models/profileType";
import { ProfileService } from "../profile/profile";
import { firestore } from "../../firebase";
import { SaleItem } from "../../models/saleItem";
import { TimeServices } from "../functions/timeServices";
import { RestartAlt } from "@mui/icons-material";

const RESTAURANT_COLLECTION = "restaurants"; // Replace with your Firestore collection name

export const restaurantService = {
    /**
     * Fetch every restaurant in Firestore
     * @returns every restaurant as an array in no particular order
     */
    async fetchAllRestaurants(): Promise<FullProperty[]> {
        const snapshot = await getDocs(collection(firestore, RESTAURANT_COLLECTION));
        const restaurants: FullProperty[] = [];
        snapshot.forEach((doc) => {
            const data = addMissingFullPropertyProperties(doc.data() as Partial<FullProperty>);
            restaurants.push(data);
        });
        return restaurants;
    },

    /**
     * Create a new restaurant in Firestore
     * @param restaurant - The restaurant data to be saved
     * @returns The Firestore document ID of the created restaurant
     */
    async createRestaurant(restaurant: FullProperty): Promise<string> {
        const docRef = await addDoc(collection(firestore, RESTAURANT_COLLECTION), {
            ...restaurant,
            location: {
                ...restaurant.location,
                latitude: parseFloat(restaurant.location.lat.toString()),
                longitude: parseFloat(restaurant.location.lng.toString()),
            },
        });
        // Update the document with its ID as the `location.id`
        await updateDoc(docRef, {
            "id": docRef.id,
        });
        return docRef.id;
    },

    async editRestaurant(id: string, restaurant: Partial<FullProperty>) {
        const docRef = doc(firestore, RESTAURANT_COLLECTION, id); // Reference to the specific document by ID
        await updateDoc(docRef, { ...restaurant });
    },

    async changeRestaurantActivityStatus(id: string, isActive: boolean, user: ProfileType) {
        if (!isActive) {
            await this.editRestaurant(id, {
                isActive,
                isPublished: false
            })
            // If the restaurant was deactivated, and user has no active restaurants then move the customer from owner
            // to user
            const allRestaurants = await restaurantService.fetchRestaurantsByOwner(user.id)
            let anyActiveRestaurants = false;
            for (var restaurant in allRestaurants) {
                if ((addMissingFullPropertyProperties(restaurant as any as Partial<FullProperty>).isPublished)) {
                    anyActiveRestaurants = true;
                }
            }
        } else {
            await this.editRestaurant(id, {
                isActive
            })
        }
    },

    async changeRestaurantPlantBasedTreatyStatus(retaurantId: string, isPlantBasedTreaty: boolean) {
        var restaurant = await this.fetchRestaurantById(retaurantId);
        await this.editRestaurant(retaurantId, { isPlantBasedTreaty })
    },

    /**
     * Fetch a specific restaurant by id
     * @param id - The restaurants id
     * Updates a specific restaurant's sale item end date as soon as a restaurant is editing or created with auto trade enabled.
     * NB This is almost certainly uncessary for us to do.
     * TODO: Remove this after discussion.
     */
    async updateSaleItemsEndDatesOnAutoTradeEnabled(id: string, isAutoTradeEnabled: boolean, user: ProfileType) {
        if (isAutoTradeEnabled) {
            var restaurant = await this.fetchRestaurantById(id);
            var newSaleItems: SaleItem[] = [];
            let endDate: string | undefined;
            let startDate: string | undefined;
            const now = new Date();
            const operatingDay = TimeServices.getOperatingDayType(now);
            switch (operatingDay) {
                case 'public-holiday':
                    endDate = restaurant.closingTime;
                    break;
                case 'monday':
                    endDate = restaurant.mondayEnd;
                    startDate = restaurant.mondayStart;
                    break;
                case 'tuesday':
                    endDate = restaurant.tuesdayEnd;
                    startDate = restaurant.tuesdayStart;
                    break;
                case 'wednesday':
                    endDate = restaurant.wednesdayEnd;
                    startDate = restaurant.wednesdayStart;
                    break;
                case 'thursday':
                    endDate = restaurant.thursdayEnd;
                    startDate = restaurant.thursdayStart;
                    break;
                case 'friday':
                    endDate = restaurant.fridayEnd;
                    startDate = restaurant.fridayStart;
                    break;
                case 'saturday':
                    endDate = restaurant.saturdayEnd;
                    startDate = restaurant.saturdayStart;
                    break;
                case 'sunday':
                    endDate = restaurant.sundayEnd;
                    startDate = restaurant.sundayStart;
                    break;
                case 'public-holiday':
                    endDate = restaurant.holidayEnd;
                    startDate = restaurant.holidayStart;
                    break;
                default:
                    endDate = restaurant.closingTime; // fallback
                    break;
            }

            // The start and end times above will have the incorrect date.
            // i.e It will be the date that the times were editing on the create restaurant page.
            // This is why, the following two lines are necessary in order to get new dates.
            // We have decided that, when the restaurant is saved here, the start and end dates should be updated in the background to the current date.
            // Refer to the syncAutoTradeCollectionWindows function in the firebase functions folder for more information.
            // This is somewhat of a duplication of the logic for now. The important thing is that we do the same thing here during the create restaurant process.
            var currentStartTime = TimeServices.getCurrentClosingTime(new Date(startDate ?? ''));
            var currentClosingTime = TimeServices.getCurrentClosingTime(new Date(endDate ?? ''));

            for (const saleItem of restaurant.saleItems) {
                saleItem.endDate = currentClosingTime.toISOString() ?? '';
                saleItem.startOfCollectionTime = currentStartTime.toISOString() ?? '';
                saleItem.startOfCollectionTimeDayDeltaFromNow = 0;
                saleItem.endDateDayDeltaFromNow = 0;
                newSaleItems.push(saleItem)
            }
            await restaurantService.editRestaurant(restaurant.id, { saleItems: newSaleItems });
        }
    },

    /**
     * Fetch a specific restaurant by id
     * @param id - The restaurants id
     * @returns A FullProperty object
     */
    async fetchRestaurantById(id: string): Promise<FullProperty> {
        const docRef = doc(firestore, RESTAURANT_COLLECTION, id); // Reference to the specific document by ID
        const snapshot = await getDoc(docRef);

        if (!snapshot.exists()) {
            throw new Error(`Restaurant with ID ${id} not found.`);
        }

        const data = snapshot.data();
        return addMissingFullPropertyProperties(data as Partial<FullProperty>); // Convert Firestore data to FullProperty instance
    },


    /**
     * Fetch all restaurants associated with a specific owner
     * @param ownerId - The owner's UID
     * @returns An array of FullProperty objects
     */
    async fetchRestaurantsByOwner(ownerId: string): Promise<FullProperty[]> {
        const q = query(collection(firestore, RESTAURANT_COLLECTION), where("owner", "==", ownerId));
        const snapshot = await getDocs(q);
        const restaurants: FullProperty[] = [];
        snapshot.forEach((doc) => {
            const data = doc.data();
            restaurants.push(addMissingFullPropertyProperties(data as Partial<FullProperty>));
        });
        return restaurants;
    },

    /**
     * Fetch all restaurants associated with a specific staff member
     * @param ownerId - The owner's UID
     * @returns An array of FullProperty objects
     */
    async fetchRestaurantsByStaff(staffId: string): Promise<FullProperty[]> {
        const q = query(collection(firestore, RESTAURANT_COLLECTION), where("staff", "array-contains", staffId));
        const snapshot = await getDocs(q);
        const restaurants: FullProperty[] = [];
        snapshot.forEach((doc) => {
            const data = doc.data();
            restaurants.push(addMissingFullPropertyProperties(data as Partial<FullProperty>));
        });
        return restaurants;
    },

    async fetchStaffByRestaurant(restaurant: FullProperty): Promise<ProfileType[]> {
        let profiles: ProfileType[] = [];
        await Promise.all(restaurant.staff.map(async (uid) => {
            const profile = await ProfileService.getProfile(uid);
            if (profile) {
                profiles.push(profile);
            }
        }));
        return profiles;
    },

    /**
     * Search for restaurants sorted by proximity to a given location
     * @param latitude - The latitude of the customer's location
     * @param longitude - The longitude of the customer's location
     * @returns An array of FullProperty objects sorted by Euclidean distance
     */
    async searchRestaurantsByProximity(
        latitude: number,
        longitude: number,
        categoryFilter?: string
    ): Promise<DistanceSearchItem[]> {
        const snapshot = await getDocs(collection(firestore, RESTAURANT_COLLECTION));
        const restaurants: DistanceSearchItem[] = [];

        const isClosed = ({ restaurant }: { restaurant: FullProperty }): boolean => {
            const totalQuantity = getTotalQuantity(restaurant.saleItems);
            if (totalQuantity == null) return true;
            if (!restaurant.closingTime) return true;

            return TimeServices.isClosed(restaurant) && totalQuantity <= 0;
        };

        const getTotalQuantity = (saleItems: SaleItem[]): number | null => {
            var total = saleItems.reduce((total, item) => total + item.available, 0);
            return total;
        };

        snapshot.forEach((doc) => {
            const data = addMissingFullPropertyProperties(doc.data() as Partial<FullProperty>);
            const location = data.location;
            if (location && location.lat && location.lng && data.isActive && data.isPublished) {
                const distance = Math.sqrt(
                    Math.pow(parseFloat(location.lat.toString()) - latitude, 2) +
                    Math.pow(parseFloat(location.lng.toString()) - longitude, 2)
                );

                const isClosedNow = isClosed({ restaurant: data });

                restaurants.push({
                    restaurant: data,
                    distance,
                    isClosed: isClosedNow,
                });
            }
        });

        return restaurants.sort((a, b) => {
            if (a.isClosed === b.isClosed) {
                return b.distance - a.distance;
            }
            return a.isClosed ? 1 : -1;
        });
    },

sortRestaurants(
  restaurants: DistanceSearchItem[],
  selectedCategory?: string
): DistanceSearchItem[] {
  const normalizedCategory = selectedCategory?.trim().toLowerCase() || "";

  // 🔹 First, normalize and categorize everything (process all items)
  const matchingCategory: DistanceSearchItem[] = [];
  const otherCategory: DistanceSearchItem[] = [];

  for (const item of restaurants) {
    const category = item.restaurant.category?.toLowerCase() || "";

    if (normalizedCategory && category === normalizedCategory) {
      matchingCategory.push(item);
    } else {
      otherCategory.push(item);
    }
  }

  // 🔹 Sort both groups independently — ensures all items are handled
  const sortByOpenThenDistance = (a: DistanceSearchItem, b: DistanceSearchItem) => {
    if (a.isClosed !== b.isClosed) return a.isClosed ? 1 : -1;
    return a.distance - b.distance;
  };

  matchingCategory.sort(sortByOpenThenDistance);
  otherCategory.sort(sortByOpenThenDistance);

  // 🔹 Combine results — ensures *all* items are sorted and included
  return [...matchingCategory, ...otherCategory];
},


    fetchSaleItems: async (restaurantId: string): Promise<SaleItem[]> => {
        const restaurant = await restaurantService.fetchRestaurantById(restaurantId);
        return restaurant.saleItems;
    },
    addSaleItem: async (restaurantId: string, saleItem: SaleItem): Promise<void> => {
        const restaurant = await restaurantService.fetchRestaurantById(restaurantId);
        await restaurantService.editRestaurant(restaurantId, { saleItems: [...restaurant.saleItems, saleItem] })
    },
    // removeSaleItem: async (restaurantId: string, saleItem: SaleItem): Promise<SaleItem[]> => {
    //     const restaurant = await restaurantService.fetchRestaurantById(restaurantId);
    //     console.log(restaurant.saleItems)
    //     let newSaleItems: SaleItem[] = [];
    //     restaurant.saleItems.forEach((item) => { if(item.id !== saleItem.id) {
    //         newSaleItems.push(item);
    //     }})
    //     console.log(newSaleItems)
    //     await restaurantService.editRestaurant(restaurantId, { saleItems: newSaleItems })
    //     console.log(newSaleItems)
    //     return newSaleItems
    // },
    getFinancialSummary: (restaurant: FullProperty): {
        lifetimeValue: number,
        lifetimePaid: number,
        nextPayment: number, // Date Time
    } => {
        // TODO:
        // This function will be moved to firebase functions, but here for Dev Speed
        var lifetimePaid: number = 0
        restaurant.paymentHistory.forEach((pe: PaymentEvent) => {
            lifetimePaid += pe["amount"]
        })
        const lifetimeValue = restaurant.totalSalesInZarC - restaurant.totalRefundsInZarC
        return {
            lifetimePaid,
            lifetimeValue,
            nextPayment: lifetimeValue - lifetimePaid
        }
    },
    addPaymentToRestaurant: async (restaurant: FullProperty, paymentDate: string, amount: number) => {
        let payments = [
            { amount, paymentDate } as PaymentEvent,
            ...restaurant.paymentHistory]
        await restaurantService.editRestaurant(restaurant.id, { paymentHistory: payments })
    }
};
